/**
 * The wizard step sequence.
 *
 * Steps are composed per proposal: the engagement step only appears when the
 * selected family declares fields, and the scope step only for a Customize
 * proposal. A CoE Quick Proposal therefore shows four steps; a Managed
 * Customize proposal shows six. The user never sees a screen that does not
 * apply to the service they are selling.
 */
export const ALL_STEPS = {
  client: { id: 'client', label: 'Client & dates', hint: 'Who, when, and for how long' },
  engagement: { id: 'engagement', label: 'Engagement', hint: 'Details for this service' },
  scope: { id: 'scope', label: 'Scope', hint: 'Adjust approved content' },
  timeline: { id: 'timeline', label: 'Timeline', hint: 'Duration per approved phase' },
  commercials: { id: 'commercials', label: 'Commercials', hint: 'Amounts and payment terms' },
  review: { id: 'review', label: 'Review', hint: 'Check, then generate' },
} as const;

export type StepId = keyof typeof ALL_STEPS;

export interface WizardStep {
  id: StepId;
  label: string;
  hint: string;
}

/** Which steps this proposal actually needs. */
export function stepsFor(options: {
  hasEngagementFields: boolean;
  isCustomMode: boolean;
}): WizardStep[] {
  const steps: WizardStep[] = [ALL_STEPS.client];
  if (options.hasEngagementFields) steps.push(ALL_STEPS.engagement);
  if (options.isCustomMode) steps.push(ALL_STEPS.scope);
  steps.push(ALL_STEPS.timeline, ALL_STEPS.commercials, ALL_STEPS.review);
  return steps;
}

export const nextStep = (steps: WizardStep[], current: StepId): StepId | null => {
  const index = steps.findIndex((s) => s.id === current);
  return index >= 0 && index < steps.length - 1 ? (steps[index + 1]!.id) : null;
};

export const previousStep = (steps: WizardStep[], current: StepId): StepId | null => {
  const index = steps.findIndex((s) => s.id === current);
  return index > 0 ? steps[index - 1]!.id : null;
};
